import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, ShieldCheck, Flame, ArrowLeftRight, Users, MapPin,
  CheckCircle2, Clock, XCircle, AlertCircle, Plus, Edit3, Save,
  Bell, ChevronRight, Sparkles, ArrowLeft, Info, ScrollText,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────────
   PREVIEW / MOCKUP ONLY — no backend calls, no DB writes.
   Purpose: let the user visually approve the concept of a second tracker
   ("Branch Licenses") before we build it for real.
──────────────────────────────────────────────────────────────────────────── */

const SAMPLE_LICENSES = [
  { id: "b1", code: "AJD078",  name: "Madina",             am: "Abdallah G", sup: "Sulaiman", biz_exp: "1447-06-20", cd_exp: "1447-05-01" },
  { id: "b2", code: "AJD285",  name: "Naqaa Madinah Road", am: "Abdallah G", sup: "Vejish",   biz_exp: "1448-02-10", cd_exp: "1447-11-05" },
  { id: "b3", code: "AJD146",  name: "TAHLIA 02",          am: "Abdallah G", sup: "Vejish",   biz_exp: "1449-01-15", cd_exp: "1448-08-20" },
  { id: "b4", code: "AJD319",  name: "Amanah Rd",          am: "Abdallah G", sup: "Sulaiman", biz_exp: "1447-04-02", cd_exp: "1448-03-10" },
  { id: "b5", code: "AJD065",  name: "SULTAN 03",          am: "Abdallah G", sup: "Vejish",   biz_exp: "1447-08-12", cd_exp: "1447-07-18" },
  { id: "b6", code: "AJD028",  name: "Manar",              am: "Abdallah G", sup: "Vejish",   biz_exp: "1448-05-30", cd_exp: "1449-01-01" },
];

function daysUntilHijri(hijri) {
  // Naive mock: parse YYYY-MM-DD hijri, subtract from today's Hijri (16/01/1448).
  // We're only doing this for the preview badge — real endpoint will compute properly.
  const today = { y: 1448, m: 1, d: 16 };
  const [y, m, d] = hijri.split("-").map(Number);
  const totalToday = today.y * 354 + today.m * 29.5 + today.d;
  const totalTgt = y * 354 + m * 29.5 + d;
  return Math.round(totalTgt - totalToday);
}

function StatusPill({ hijri }) {
  const d = daysUntilHijri(hijri);
  let cls = "bg-emerald-100 text-emerald-800 border-emerald-300";
  let Icon = CheckCircle2;
  let label = `سارٍ · ${d} يوم`;
  if (d < 0) { cls = "bg-rose-100 text-rose-700 border-rose-300"; Icon = XCircle; label = `منتهية منذ ${Math.abs(d)} يوم`; }
  else if (d <= 30) { cls = "bg-amber-100 text-amber-800 border-amber-300"; Icon = Clock; label = `تنتهي خلال ${d} يوم`; }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${cls}`}>
      <Icon size={11} /> {label}
    </span>
  );
}

function formatH(h) {
  const [y, m, d] = h.split("-");
  return `${d}/${m}/${y}`;
}

/* ─────────── Section 1: Post-login module chooser ─────────── */
function ModuleChooserMock({ onChoose }) {
  const cards = [
    {
      key: "employees",
      title: "شهادات الموظفين الصحية",
      subtitle: "الشهادة الصحية · التثقيف · كرد البلدية · صورة مطبوعة",
      icon: ShieldCheck,
      color: "from-teal-500 to-emerald-600",
      badge: "المتتبع الحالي",
      stats: [
        { label: "موظف", value: 89 },
        { label: "منتهية", value: 6, danger: true },
        { label: "قاربت", value: 21, warn: true },
      ],
    },
    {
      key: "licenses",
      title: "رخص الفروع",
      subtitle: "رخصة الفرع · رخصة الدفاع المدني — تنبيه بتاريخ الانتهاء",
      icon: Building2,
      color: "from-indigo-500 to-fuchsia-600",
      badge: "جديد — قيد المعاينة",
      stats: [
        { label: "فرع", value: 23 },
        { label: "منتهية", value: 2, danger: true },
        { label: "قاربت", value: 5, warn: true },
      ],
    },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {cards.map((c) => (
        <button
          key={c.key}
          onClick={() => onChoose(c.key)}
          className="group relative overflow-hidden text-start rounded-2xl bg-white border-2 border-slate-200 hover:border-teal-400 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
          data-testid={`mock-module-${c.key}`}
        >
          <div className={`h-1.5 bg-gradient-to-l ${c.color}`} />
          <div className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${c.color} text-white shadow-lg group-hover:scale-110 transition-transform`}>
                <c.icon size={26} />
              </div>
              <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${c.key === "licenses" ? "bg-fuchsia-100 text-fuchsia-800" : "bg-teal-100 text-teal-800"}`}>
                {c.badge}
              </span>
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-1">{c.title}</h3>
            <p className="text-sm text-slate-500 mb-4">{c.subtitle}</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {c.stats.map((s) => (
                <div key={s.label} className={`rounded-xl px-2 py-2 text-center ${s.danger ? "bg-rose-50" : s.warn ? "bg-amber-50" : "bg-slate-50"}`}>
                  <div className={`text-lg font-black ${s.danger ? "text-rose-700" : s.warn ? "text-amber-700" : "text-slate-800"}`}>{s.value}</div>
                  <div className="text-[10px] text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">اضغط للدخول</span>
              <ChevronRight size={16} className="text-teal-700 group-hover:translate-x-[-4px] transition-transform" />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

/* ─────────── Section 2: Branch License dashboard mockup ─────────── */
function BranchLicensesDashboardMock() {
  const kpis = [
    { label: "إجمالي الفروع",       value: 23, icon: Building2, tone: "text-slate-800", bg: "bg-slate-50" },
    { label: "منتهية",              value: 2,  icon: XCircle,   tone: "text-rose-700",  bg: "bg-rose-50" },
    { label: "قاربت (خلال 30 يوم)", value: 5,  icon: Clock,     tone: "text-amber-700", bg: "bg-amber-50" },
    { label: "سارية",               value: 16, icon: CheckCircle2, tone: "text-emerald-700", bg: "bg-emerald-50" },
  ];
  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className={`${k.bg} rounded-2xl p-4 border border-slate-100`}>
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-3xl font-black ${k.tone}`}>{k.value}</div>
                <div className="text-xs text-slate-500 mt-1">{k.label}</div>
              </div>
              <k.icon className={`${k.tone} opacity-60`} size={26} />
            </div>
          </div>
        ))}
      </div>

      {/* Branch licenses table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-l from-indigo-600 to-fuchsia-600 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScrollText size={18} />
            <h3 className="font-bold">ترخيصات الفروع</h3>
          </div>
          <button className="btn btn-secondary text-xs bg-white/20 border-white/30 text-white hover:bg-white/30">
            <Plus size={13} /> ترخيص جديد
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="p-3 text-start font-bold text-slate-600">الفرع</th>
                <th className="p-3 text-start font-bold text-slate-600">مدير المنطقة</th>
                <th className="p-3 text-start font-bold text-slate-600">المشرف</th>
                <th className="p-3 text-start font-bold text-slate-600">
                  <span className="inline-flex items-center gap-1"><Building2 size={13} className="text-indigo-600" /> رخصة الفرع</span>
                </th>
                <th className="p-3 text-start font-bold text-slate-600">
                  <span className="inline-flex items-center gap-1"><Flame size={13} className="text-rose-600" /> الدفاع المدني</span>
                </th>
                <th className="p-3 text-start font-bold text-slate-600"></th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_LICENSES.map((r, i) => (
                <tr key={r.id} className={i % 2 ? "bg-slate-50/40" : ""}>
                  <td className="p-3">
                    <div className="font-semibold text-slate-800">{r.name}</div>
                    <div className="text-[11px] text-slate-400">[{r.code}]</div>
                  </td>
                  <td className="p-3 text-slate-600">👑 {r.am}</td>
                  <td className="p-3 text-slate-600">{r.sup}</td>
                  <td className="p-3">
                    <div className="font-mono text-slate-800">{formatH(r.biz_exp)}</div>
                    <div className="mt-1"><StatusPill hijri={r.biz_exp} /></div>
                  </td>
                  <td className="p-3">
                    <div className="font-mono text-slate-800">{formatH(r.cd_exp)}</div>
                    <div className="mt-1"><StatusPill hijri={r.cd_exp} /></div>
                  </td>
                  <td className="p-3">
                    <button className="text-slate-400 hover:text-teal-700 p-1.5 rounded-lg hover:bg-teal-50">
                      <Edit3 size={15} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Section 3: Add/Edit modal preview ─────────── */
function EditModalMock() {
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden max-w-xl mx-auto">
      <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2"><Building2 size={16} /> تعديل ترخيصات الفرع — Madina [AJD078]</h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/30 border border-fuchsia-300">معاينة</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-900 flex items-start gap-2">
          <Info size={14} className="mt-0.5 flex-shrink-0" />
          <span>سيظهر هذا الفرع في تنبيهات «رخص الفروع» تلقائياً قبل انتهاء أي من الرخصتين بـ 30 يوم (قابل للتعديل).</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block flex items-center gap-1">
              <Building2 size={12} className="text-indigo-600" /> رخصة الفرع (هجري)
            </label>
            <div className="flex gap-2">
              <input type="text" value="20" readOnly className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
              <input type="text" value="06" readOnly className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
              <input type="text" value="1447" readOnly className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">يوم · شهر · سنة</div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block flex items-center gap-1">
              <Flame size={12} className="text-rose-600" /> رخصة الدفاع المدني (هجري)
            </label>
            <div className="flex gap-2">
              <input type="text" value="01" readOnly className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
              <input type="text" value="05" readOnly className="w-16 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
              <input type="text" value="1447" readOnly className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-center" />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold text-slate-600 mb-1 block">ملاحظات (اختياري)</label>
            <textarea rows={2} readOnly className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="أي ملاحظات حول الرخصة…" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg p-2 text-xs text-emerald-900">
            <ArrowLeftRight size={13} /> يمكن إرفاق صور الرخص كملفات مرفقة (نفس آلية شهادات الموظفين).
          </div>
        </div>
      </div>
      <div className="px-5 py-3 bg-slate-50 border-t flex items-center justify-end gap-2">
        <button className="btn btn-secondary" disabled>إلغاء</button>
        <button className="btn btn-primary" disabled><Save size={14} /> حفظ</button>
      </div>
    </div>
  );
}

/* ─────────── Main preview page ─────────── */
export default function LicensesPreview() {
  const navigate = useNavigate();
  const [showChooser, setShowChooser] = useState(true);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-fuchsia-50/30">
      {/* Banner */}
      <div className="bg-gradient-to-l from-amber-500 to-orange-500 text-white px-5 py-3 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={18} />
            <div>
              <div className="font-bold text-sm">معاينة أولية — تتبع رخص الفروع (Mockup)</div>
              <div className="text-[11px] opacity-90">هذه صفحة تجريبية للتصور فقط — لا يوجد بيانات حقيقية ولا تخزين. لو حبيت التصميم، أخبرني وأقوم بتنفيذه فعلياً.</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/")}
            className="btn btn-secondary bg-white/20 border-white/30 text-white hover:bg-white/30 text-xs"
            data-testid="mock-back-to-app"
          >
            <ArrowLeft size={13} /> رجوع للتطبيق
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-5 space-y-8">
        {/* Section 1 header */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-teal-700 text-white text-sm font-bold flex items-center justify-center">1</span>
            <h2 className="text-xl font-black text-slate-800">شاشة الاختيار — تظهر مباشرة بعد تسجيل الدخول</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4 px-9">يختار المستخدم أي متتبع يريد الدخول له. يمكن التنقل بينهما لاحقاً من القائمة الجانبية.</p>
          <div className="px-9">
            {showChooser ? (
              <ModuleChooserMock onChoose={(k) => {
                if (k === "employees") window.alert("سيذهب المستخدم إلى لوحة تتبع الموظفين (المتتبع الحالي).");
                else setShowChooser(false);
              }} />
            ) : (
              <div className="flex items-center gap-2">
                <button className="btn btn-secondary text-xs" onClick={() => setShowChooser(true)}>
                  <ArrowLeft size={13} /> عودة لشاشة الاختيار
                </button>
                <div className="text-xs text-slate-500">مثال: اخترت «رخص الفروع» — انتقلت إلى لوحة التحكم أدناه.</div>
              </div>
            )}
          </div>
        </section>

        {/* Section 2 */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold flex items-center justify-center">2</span>
            <h2 className="text-xl font-black text-slate-800">لوحة تحكم رخص الفروع</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4 px-9">
            نفس بنية المتتبع الحالي — <b>مدير منطقة → مشرف → فرع</b> — مع عمودين للرخصتين وتاريخ انتهاء هجري لكل منهما.
          </p>
          <div className="px-9">
            <BranchLicensesDashboardMock />
          </div>
        </section>

        {/* Section 3 */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-fuchsia-600 text-white text-sm font-bold flex items-center justify-center">3</span>
            <h2 className="text-xl font-black text-slate-800">نموذج تعديل ترخيصات الفرع</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4 px-9">يستخدمه مدير المنطقة أو المشرف — تاريخ هجري لكل رخصة، مع إمكانية إرفاق صور.</p>
          <div className="px-9">
            <EditModalMock />
          </div>
        </section>

        {/* Section 4: Notes */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center">4</span>
            <h2 className="text-xl font-black text-slate-800">ماذا سيتم عمله عند اعتماد التصميم؟</h2>
          </div>
          <div className="px-9 space-y-2 text-sm">
            <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-xl p-3">
              <MapPin className="text-indigo-600 flex-shrink-0 mt-0.5" size={16} />
              <div><b>حقول جديدة على الفرع</b>: <code>business_license_expiry_hijri</code> و <code>civil_defense_expiry_hijri</code> + مرفقات.</div>
            </div>
            <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-xl p-3">
              <Bell className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
              <div><b>تنبيهات موحّدة</b>: تضاف تنبيهات الرخص إلى نفس نظام التنبيهات الحالي (نفس نطاق التنبيه القابل للتعديل).</div>
            </div>
            <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-xl p-3">
              <Users className="text-teal-600 flex-shrink-0 mt-0.5" size={16} />
              <div><b>نفس الصلاحيات</b>: سوبر ادمن يرى الكل، مدير المنطقة يرى فروعه، المشرف يرى فروعه فقط.</div>
            </div>
            <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-xl p-3">
              <ScrollText className="text-slate-600 flex-shrink-0 mt-0.5" size={16} />
              <div><b>Excel</b>: تصدير واستيراد مماثل لشهادات الموظفين — قالب فارغ + upsert.</div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="rounded-2xl bg-gradient-to-l from-teal-600 to-emerald-600 text-white p-5 text-center shadow-xl">
          <h3 className="text-lg font-black mb-1">هل تعجبك الفكرة؟</h3>
          <p className="text-sm opacity-90 mb-3">لو موافق ابعث لي «تمام نفّذها» وأبدأ ببناء الـ Backend وقاعدة البيانات فعلياً.</p>
          <button
            onClick={() => navigate("/")}
            className="btn bg-white text-teal-700 hover:bg-teal-50 font-bold"
            data-testid="mock-cta-back"
          >
            <ArrowLeft size={14} /> رجوع للتطبيق الحقيقي
          </button>
        </div>
      </div>
    </div>
  );
}
